import React, { useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link, AccessType } from "@quintype/components";
import { navigateTo, getLinkTarget } from "../../../../arrow/utils/utils";
import get from "lodash/get";
import { logout } from "@quintype/bridgekeeper-js";
import { OPEN_SEARCHBAR, MEMBER_UPDATED, SUBSCRIBER_METADATA_UPDATED, MOBILE_MENU_TOGGLED } from "../../../store/actions";
import { STATIC_KEYWORDS, NAV_LINKS } from "../../../../constants";

import HamburgerIcon from "../../../../../assets/icons/hamburger";
import SearchIcon from "../../../../../assets/icons/searchicon1";
import CloseIcon from "../../../atoms/CloseIcon";

import { DesktopNavItem } from "./DesktopNavItem";
import { MobileMenuItem } from "./MobileNavItem";
import { SearchOverlay } from "../search-overlay";
import { AppLogo } from "../../app-logo";
import { SignInModal } from "../sign-in-modal";

import "./top-bar.m.css";
import DropdownArrow from "../../../../../assets/icons/dropdown";

const TopBarWithAccessType = ({ getSubscriberMetadata, setSubscriberMetadata, isATGlobal, initAccessType }) => {
  const dispatch = useDispatch();
  const menu = get(useSelector(state => state), ["qt", "data", "navigationMenu", "homeMenu"], []);
  const member = useSelector(state => get(state, ["member"], null));
  const reduxSubscriberMetadata = useSelector((state) => get(state, ["subscriberMetadata"], null));
  const publisherSettings = useSelector(state => get(state, ["qt", "config", "publisher-settings"], {}));
  const publisherLogo = publisherSettings["publisher-logo"];
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [expandedItems, setExpandedItems] = useState({});
  const [isSignInModalOpen, setIsSignInModalOpen] = useState(false);
  const [isUserDropdownOpen, setIsUserDropdownOpen] = useState(false);
  const [metadataLoading, setMetadataLoading] = useState(false);
  const [metadataFetched, setMetadataFetched] = useState(false);
  const { LOGO_TEXT, LOGO_TEXT_FULL, CONTACT_US, SIGN_IN, BECOME_MEMBER, CLOSE_MENU, OPEN_MENU, MEMBER_DIRECTORY, ACCOUNT } = STATIC_KEYWORDS;
  const { contactUs, subscription, memberDirectory, account, memberPage } = NAV_LINKS;
  const toggleSearchHandler = () => dispatch({ type: OPEN_SEARCHBAR, isSearchBarOpen: true });
  const toggleMobileMenu = () => setIsMobileMenuOpen(prev => {
    const newState = !prev;
    document.body.style.overflow = newState ? "hidden" : "unset";
    if (newState) {
      document.body.classList.add("mobile-menu-open");
    } else {
      document.body.classList.remove("mobile-menu-open");
    }
    // Dispatch to Redux store
    dispatch({ type: MOBILE_MENU_TOGGLED, isOpen: newState });
    return newState;
  });
  const toggleMenuItem = (key, closeSiblings = []) => {
    setExpandedItems(prev => {
      const newState = { ...prev };
      // Close all siblings if provided
      if (closeSiblings.length > 0) {
        closeSiblings.forEach(siblingKey => {
          if (siblingKey !== key) {
            newState[siblingKey] = false;
          }
        });
      }
      // Toggle the clicked item
      newState[key] = !prev[key];
      return newState;
    });
  };
  const handleMobileLinkClick = () => {
    setIsMobileMenuOpen(false);
    document.body.style.overflow = "unset";
    document.body.classList.remove("mobile-menu-open");
    // Dispatch to Redux store
    dispatch({ type: MOBILE_MENU_TOGGLED, isOpen: false });
  };

  const handleSignInClick = (e) => {
    e.preventDefault();
    setIsSignInModalOpen(true);
  };

  const handleSignInModalClose = () => {
    setIsSignInModalOpen(false);
  };


  const getUserDisplayName = () => {
    if (!member) return null;
    return reduxSubscriberMetadata?.['first-name'] || member?.['first-name'] || member.name;
  };

  const handleUserNameClick = () => {
    setIsUserDropdownOpen(prev => !prev);
  };

  const handleDashboardClick = () => {
    setIsUserDropdownOpen(false);
    navigateTo(dispatch, "/member-dashboard");
  };

  const handleLogoutClick = async () => {
    setIsUserDropdownOpen(false);
    try {
      await logout();
      dispatch({ type: MEMBER_UPDATED, member: null });
      navigateTo(dispatch, "/");
    } catch (error) { }
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isUserDropdownOpen && !event.target.closest('[data-user-dropdown]')) {
        setIsUserDropdownOpen(false);
      }
    };

    if (isUserDropdownOpen) {
      document.addEventListener('click', handleClickOutside);
      return () => document.removeEventListener('click', handleClickOutside);
    }
  }, [isUserDropdownOpen]);

  // Fetch subscriber metadata for logged-in users
  useEffect(() => {
    // First check if metadata exists in Redux (from sign-in)
    if (reduxSubscriberMetadata) {
      setMetadataFetched(true);
      setMetadataLoading(false);
      return;
    }

    // Only fetch if not available in Redux and haven't fetched before
    if (isATGlobal && member && !metadataFetched && !metadataLoading && !reduxSubscriberMetadata) {
      initAccessType(() => {
        setMetadataLoading(true);
        getSubscriberMetadata()
          .then((response) => {
            const metadata = response?.data?.other || {};
            // Store in Redux for other components
            dispatch({ type: SUBSCRIBER_METADATA_UPDATED, metadata });
          })
          .catch((error) => {
            console.error('Error fetching subscriber metadata:', error);
            dispatch({ type: SUBSCRIBER_METADATA_UPDATED, metadata: {} });
          })
          .finally(() => {
            setMetadataLoading(false);
            setMetadataFetched(true);
          });
      });
    }
  }, [isATGlobal, member, reduxSubscriberMetadata, metadataFetched, metadataLoading, dispatch]);

  // Find and expand active menu items based on current URL
  useEffect(() => {
    if (typeof window === "undefined") return;

    const currentPath = window.location.pathname;
    const findActiveItem = (items, parentKey = "") => {
      for (const item of items) {
        const keyName = parentKey ? `${parentKey}-${item.title}` : item.title;
        if (item.completeUrl === currentPath) {
          return { keyName, parentKey };
        }
        if (item.children?.length > 0) {
          const result = findActiveItem(item.children, keyName);
          if (result) return result;
        }
      }
      return null;
    };

    const activeItem = findActiveItem(menu);
    if (activeItem && activeItem.parentKey) {
      const keysToExpand = {};
      const parts = activeItem.parentKey.split('-');
      let currentKey = "";
      parts.forEach(part => {
        currentKey = currentKey ? `${currentKey}-${part}` : part;
        keysToExpand[currentKey] = true;
      });
      setExpandedItems(keysToExpand);
    }
  }, [menu]);

  const [activeDesktopItem, setActiveDesktopItem] = useState(null);
  const desktopNavRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (desktopNavRef.current && !desktopNavRef.current.contains(event.target)) {
        setActiveDesktopItem(null);
      }
    };

    if (activeDesktopItem) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [activeDesktopItem]);

  const handleDesktopToggle = (title) => {
    setActiveDesktopItem(prev => prev === title ? null : title);
  };

  // Add opacity effect to entire page when desktop menu is open
  useEffect(() => {
    if (activeDesktopItem) {
      document.body.classList.add("desktop-menu-open");
    } else {
      document.body.classList.remove("desktop-menu-open");
    }

    return () => {
      document.body.classList.remove("desktop-menu-open");
    };
  }, [activeDesktopItem]);

  return (
    <div styleName="main-wrapper">
      <div className="container" styleName="wrapper">
        <div styleName="logo-nav-section">
          <div styleName="logo-wrapper" className="article-printable">
            <AppLogo
              imgSrc={publisherLogo?.url}
              width={72}
              height={36}
              className="article-printable-logo"
            />
          </div>
          <nav styleName="desktop-nav" ref={desktopNavRef}>
            {menu.slice(0, 6).map(item => (
              <DesktopNavItem key={item.title} item={item}
                isOpen={activeDesktopItem === item.title}
                onToggle={() => handleDesktopToggle(item.title)}
              />
            ))}
          </nav>
        </div>

        <div styleName="secondary-nav">
          <button onClick={toggleSearchHandler} styleName="search-btn" aria-label="Search">
            <SearchIcon />
          </button>
          <Link href={contactUs} styleName="contact-link" target={getLinkTarget(contactUs)} rel="noopener">{CONTACT_US}</Link>
          {member ? (
            <div styleName={`user-dropdown${isUserDropdownOpen ? " open" : ""}`} data-user-dropdown>
              <button onClick={handleUserNameClick} styleName="user-name-button" type="button" title="Click to view options">
                <span styleName="user-name-text">{getUserDisplayName()}</span>
                <span styleName="dropdown-arrow"><DropdownArrow /></span>
              </button>
              {isUserDropdownOpen && (
                <div styleName="user-dropdown-menu">
                  <button onClick={handleDashboardClick} styleName="dropdown-option">Dashboard</button>
                  <button onClick={handleLogoutClick} styleName="dropdown-option">Logout</button>
                </div>
              )}
            </div>
          ) : (
            <button onClick={handleSignInClick} styleName="sign-in-link" type="button">{SIGN_IN}</button>
          )}
          {!member && <Link href={memberPage} styleName="member-btn" target={getLinkTarget(memberPage)} rel="noopener">{BECOME_MEMBER}</Link>}
        </div>

        <div styleName="mobile-nav">
          <div styleName="logo-wrapper">
            <div styleName="mobile-logo-img">
              <AppLogo
                imgSrc={publisherLogo?.url}
                width={72}
                height={36}
                className="article-printable-logo"
              />
            </div>
          </div>
          <div styleName="mobile-actions">
            <button onClick={toggleSearchHandler} styleName="search-btn" aria-label="Search">
              <SearchIcon />
            </button>
            {member ? (
              <div styleName={`user-dropdown mobile${isUserDropdownOpen ? " open" : ""}`} data-user-dropdown>
                <button onClick={handleUserNameClick} styleName="user-name-button mobile" type="button" title="Click to view options">
                  <span styleName="user-name-text">{getUserDisplayName()}</span>
                  <span styleName="dropdown-arrow"><DropdownArrow /></span>
                </button>
                {isUserDropdownOpen && (
                  <div styleName="user-dropdown-menu mobile">
                    <button onClick={handleDashboardClick} styleName="dropdown-option">Dashboard</button>
                    <button onClick={handleLogoutClick} styleName="dropdown-option">Logout</button>
                  </div>
                )}
              </div>
            ) : (
              <button onClick={handleSignInClick} styleName="sign-in-link" type="button">{SIGN_IN}</button>
            )}
            <button
              styleName={`hamburger-btn${isMobileMenuOpen ? " hamburger-btn-open" : ""}`}
              aria-label={isMobileMenuOpen ? CLOSE_MENU : OPEN_MENU}
              onClick={toggleMobileMenu}
              type="button"
            >
              {isMobileMenuOpen ? <CloseIcon size={18} strokeWidth={1.25} /> : <HamburgerIcon />}
            </button>
          </div>
        </div>
      </div>

      {isMobileMenuOpen && <div styleName="mobile-menu-backdrop" onClick={toggleMobileMenu} />}
      {activeDesktopItem && <div styleName="desktop-menu-backdrop" onClick={() => setActiveDesktopItem(null)} />}

      <div styleName={`mobile-menu-panel${isMobileMenuOpen ? " open" : ""}`}>
        <div styleName="tablet-menu-header">
          <div styleName="tablet-menu-logo">{LOGO_TEXT_FULL}</div>

          <button
            styleName="tablet-menu-close"
            aria-label={isMobileMenuOpen ? CLOSE_MENU : OPEN_MENU}
            onClick={toggleMobileMenu}
            type="button"
          >
            {isMobileMenuOpen ? <CloseIcon size={18} strokeWidth={1.25} /> : <HamburgerIcon />}
          </button>
        </div>
        <div styleName="mobile-menu-content">
          <nav styleName="mobile-menu-nav">
            {menu.map(item => (
              <MobileMenuItem
                key={item.title}
                item={item}
                expandedItems={expandedItems}
                toggleMenuItem={toggleMenuItem}
                onLinkClick={handleMobileLinkClick}
                allSiblingKeys={menu.map(m => m.title)}
              />
            ))}
          </nav>
          <div styleName="mobile-menu-bottom">
            <Link href={contactUs} styleName="mobile-footer-link" onClick={handleMobileLinkClick} target={getLinkTarget(contactUs)} rel="noopener">
              <span>{CONTACT_US}</span>
            </Link>
            {!member && <Link href={memberPage} styleName="mobile-member-btn" onClick={handleMobileLinkClick} target={getLinkTarget(memberPage)} rel="noopener">{BECOME_MEMBER}</Link>}
          </div>
        </div>
      </div>

      <SearchOverlay />
      <SignInModal isOpen={isSignInModalOpen} onClose={handleSignInModalClose} />
    </div>
  );
};

const TopBar = () => {
  const [isATGlobal, setIsATGlobal] = useState(false);
  const member = useSelector((state) => get(state, ["member"], null));
  const email = get(member, ["email"], "");
  const phone = get(member, ["metadata", "phone-number"], "");
  const { key, accessTypeBkIntegrationId } = useSelector((state) =>
    get(state, ["qt", "config", "publisher-attributes", "accesstypeConfig"], {})
  );

  return (
    <AccessType
      enableAccesstype={true}
      accessTypeKey={key}
      email={email}
      phone={phone}
      accessTypeBkIntegrationId={accessTypeBkIntegrationId}
      onATGlobalSet={() => {
        setIsATGlobal(true);
      }}
    >
      {({ initAccessType, getSubscriberMetadata, setSubscriberMetadata }) => (
        <TopBarWithAccessType
          getSubscriberMetadata={getSubscriberMetadata}
          setSubscriberMetadata={setSubscriberMetadata}
          isATGlobal={isATGlobal}
          initAccessType={initAccessType}
        />
      )}
    </AccessType>
  );
};

export { TopBar };
